import { FC, useCallback, useMemo } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { SearchIcon } from "components/icons/Icons";
import Loading, { Sizes } from "components/ui/Loader";
import { NsKey } from "enums/NsKey";

import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { ExcludeFilter, SearchFeatureFilter, SearchPersonResult, UseSearchPersonParams } from "components/domain/SearchBar/types";
import { useSearchPerson } from "features/subscription-process/components/person-search/useSearchPerson";
import { DEFAULT_PAGE_SIZE } from "components/domain/SearchBar/constants";
import { Z_INDEX } from "shared/constants/zIndex";
import { PersonSearchBarItem } from "features/subscription-process/components/person-search/PersonSearchBarItem";

// Configuration constants
const MAX_RESULTS_HEIGHT = "max-h-80";

type SearchBarSize = 'sm' | 'md' | 'lg';

interface PersonSearchBarProps extends UseSearchPersonParams<SearchPersonResult> {
  size?: SearchBarSize;
  placeholder?: string;
  className?: string;
  excludeFilter?: ExcludeFilter<SearchPersonResult>;
}

// Size configurations (inchangé)
const SIZE_CONFIG = {
  sm: {
    input: "py-2 pl-8 pr-3 text-xs",
    icon: "h-3 w-3",
    iconContainer: "pl-2",
    results: "py-1",
    resultItem: "py-2 px-3 text-xs",
    loadingContainer: "py-2 px-3"
  },
  md: {
    input: "py-3 pl-10 pr-4 text-sm",
    icon: "h-4 w-4",
    iconContainer: "pl-3",
    results: "py-1.5",
    resultItem: "py-2.5 px-3.5 text-sm",
    loadingContainer: "py-3 px-3.5"
  },
  lg: {
    input: "shadow py-4 pl-12 pr-4 text-sm",
    icon: "h-5 w-5",
    iconContainer: "pl-4",
    results: "py-2",
    resultItem: "py-3 px-4 text-sm",
    loadingContainer: "py-4 px-4"
  }
};

export const PersonSearchBar: FC<PersonSearchBarProps> = ({
  size = 'lg',
  placeholder = "ACTOR_FORM.SEARCH_BAR.LABELS.SEARCH_ONE_OF_INSURED_PERSON",
  className,
  onSelect,
  withLegalEntity = true,
  withNaturalPerson = true,
  featureFilter = SearchFeatureFilter.Default,
  excludeFilter,
  plateformeId
}) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const {
    searchResults,
    isLoading,
    isOpen,
    totalCount,
    searchText,
    selectedItem,
    handleSelectItem,
    handleSearchChange,
    handleOpen,
    handleClose,
  } = useSearchPerson<SearchPersonResult>({
    withNaturalPerson,
    withLegalEntity,
    onSelect,
    featureFilter,
    excludeFilter,
    plateformeId
  });

  // Get size configuration
  const sizeConfig = SIZE_CONFIG[size];

  // Memoized values
  const showNoResults = useMemo(() =>
    searchResults.length === 0 && searchText.trim() !== "" && !isLoading,
    [searchResults.length, searchText, isLoading]
  );

  const displayValue = useCallback((person: SearchPersonResult | null) => {
    return person?.name || searchText;
  }, [searchText]);



  const renderSearchInput = useMemo(() => (
    <div className="relative">
      <div className={`pointer-events-none absolute inset-y-0 left-0 flex items-center ${sizeConfig.iconContainer}`}>
        <SearchIcon className={`${sizeConfig.icon} text-gray-400`} aria-hidden="true" />
      </div>
      <ComboboxInput
        className={`w-full rounded-full border bg-white ${sizeConfig.input} leading-5 text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25`}
        displayValue={displayValue}
        onChange={(event) => handleSearchChange(event.target.value)}
        placeholder={t(placeholder)}
        autoComplete="off"
        onFocus={() => {
          if (searchText.trim() && (searchResults.length > 0 || isLoading)) {
            handleOpen();
          }
        }}
      />
    </div>
  ), [displayValue, handleOpen, handleSearchChange, isLoading, placeholder, searchResults.length, searchText, sizeConfig.icon, sizeConfig.iconContainer, sizeConfig.input, t]);

  const renderSearchResults = useMemo(() => (
    <ComboboxOptions
      anchor={{ to: 'bottom', gap: 8, padding: 16 }}
      // Rendu en portal (anchor) : il doit passer AU-DESSUS des dialogs/panels —
      // cf. barème Z_INDEX (Dropdowns 100 > Dialogs 50+ > Panels 40+). Un z-50 en
      // dur le plaçait sous le contenu d'un dialog (z 51) : liste invisible.
      style={{ zIndex: Z_INDEX.DROPDOWN }}
      className={`w-[var(--input-width)] ${MAX_RESULTS_HEIGHT} overflow-auto rounded-lg bg-white ${sizeConfig.results} shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
    >
      {isLoading ? (
        <div className={`relative flex items-center justify-center ${sizeConfig.loadingContainer}`}>
          <Loading loading={isLoading} size={size === 'sm' ? Sizes.xs : Sizes.sm} isParentSized />
        </div>
      ) : showNoResults ? (
        <div className={`relative cursor-default select-none ${sizeConfig.resultItem} text-gray-700`}>
          {t("COMMON.NO_RESULT")}
        </div>
      ) : (
        <>
          {totalCount > DEFAULT_PAGE_SIZE && (
            <div className={`relative cursor-default select-none ${sizeConfig.resultItem} text-gray-500 text-xs border-b`}>
              {t("COMMON.SHOWING_RESULTS", { shown: searchResults.length, total: totalCount })}
            </div>
          )}
          {searchResults.map((person, index) => (
            <ComboboxOption
              key={index}
              className={`relative hover:bg-primary/20 group cursor-pointer select-none ${sizeConfig.resultItem} transition-colors data-[focus]:bg-primary/10 data-[focus]:text-primary text-gray-900`}
              value={person}
            >
              {({ selected, focus }) => (
                <PersonSearchBarItem
                  person={person}
                  selected={selected || selectedItem?.id === person.id}
                  size={size}
                />
              )}
            </ComboboxOption>
          ))}
        </>
      )}
    </ComboboxOptions>
  ), [isLoading, searchResults, selectedItem?.id,
    showNoResults, size, sizeConfig.loadingContainer, sizeConfig.resultItem, sizeConfig.results, t, totalCount
  ]);

  return (
    <Combobox
      value={selectedItem}
      onChange={handleSelectItem}
      onClose={handleClose}
    >
      <div className={`relative ${className}`}>
        {renderSearchInput}
        {renderSearchResults}
      </div>
    </Combobox>
  );
};
